import { CandidateEmailType, CandidatePhoneNumberType } from "../../../../App/Enums/CandidateEnums";
import { ICandidateEmailAddress, ICandidatePhoneNumber } from "../../../../App/Typings/Candidate";
import { Box, Button, IconButton, Link, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { Add, CancelOutlined, Check, Edit } from "@mui/icons-material";
import {
  convertStringToCandidateEmailType,
  emailAddressTypeOptions,
  useEditEmailState,
} from "../State/useEditEmailState";
import {
  convertStringToCandidatePhoneNumberType,
  phoneNumberTypeOptions,
  useEditPhoneState,
} from "../State/useEditPhoneState";
import { useFeaturePermission } from "../../../../App/State/Hooks/abacHooks";
import { AppFeatureLookup } from "../../../../App/Constants/AbacConstants";
import { formatPhone } from "../../../../App/Utilities/formatUtilities";
import {
  DisplayEmailAddress,
  DisplayPhoneNumber,
} from "../../../../ManageCandidates/Components/CandidateRow/CandidateRow";
import { useAddPhoneState } from "../State/useAddPhoneState";
import { useAddEmailState } from "../State/useAddEmailState";

export const DisplayAndEditEmailAddress = ({
  candidateEmailAddress,
}: {
  candidateEmailAddress: ICandidateEmailAddress;
}) => {
  const {
    getters: {
      isEditEmail,
      editEmail,
      emailAddressType,
      disableSave,
      saveButtonMessage,
      validEmailAddressEdit,
    },
    handlers: {
      setIsEditEmail,
      handleChangeEmailAddress,
      handleChangeEmailType,
      handleCancelChanges,
      handleSubmit,
    },
  } = useEditEmailState(candidateEmailAddress);

  return (
    <Box>
      {isEditEmail ? (
        <>
          <TextField
            fullWidth
            data-test="edit-candidate-email"
            label="Edit Email Address"
            value={editEmail.emailAddress}
            error={disableSave && !validEmailAddressEdit}
            sx={{ fontWeight: "normal", mt: 2 }}
            onChange={handleChangeEmailAddress}
            helperText={disableSave && !validEmailAddressEdit && "Invalid email address"}
          />
          <TextField
            fullWidth
            select
            data-test="edit-candidate-email-type"
            label="Edit Email Type"
            value={convertStringToCandidateEmailType(editEmail.emailType)}
            sx={{ fontWeight: "normal", mt: 2 }}
            onChange={handleChangeEmailType}
          >
            {emailAddressTypeOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                data-test={`edit-candidate-email-type-${option.label}`}
              >
                {option.label}
                {Number(option.value) === emailAddressType ? " (Current)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: 1, mt: 1, justifyContent: "flex-end" }}
          >
            <Tooltip title="Cancel Edit" arrow>
              <Box>
                <IconButton data-test="cancel-edit-email" color="info" onClick={() => handleCancelChanges()}>
                  <CancelOutlined />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={saveButtonMessage} arrow>
              <Box>
                <IconButton
                  color="primary"
                  data-test="submit-edit-email"
                  onClick={() => handleSubmit()}
                  disabled={disableSave}
                >
                  <Check />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        </>
      ) : (
        <Typography
          data-test="candidate-email"
          component="span"
          sx={{ fontWeight: "normal", display: "flex" }}
        >
          <DisplayEmailAddress
            email={candidateEmailAddress}
            suffix={<> ({CandidateEmailType[emailAddressType]})</>}
          />
          <IconButton
            title="Edit Email Address"
            onClick={() => setIsEditEmail(true)}
            sx={{ ml: "0.5ch" }}
            size="small"
          >
            <Edit fontSize="small" />
          </IconButton>
        </Typography>
      )}
    </Box>
  );
};

export const AddNewEmailAddress = () => {
  const {
    getters: { isAddEmail, newEmail, disableSave, saveButtonMessage, validEmailAddress },
    handlers: {
      setIsAddEmail,
      handleChangeEmailAddress,
      handleChangeEmailType,
      handleCancelChanges,
      handleSubmit,
    },
  } = useAddEmailState();

  return (
    <>
      <Box>
        {isAddEmail ? (
          <>
            <TextField
              fullWidth
              data-test="add-candidate-email"
              label="Add Email Address"
              value={newEmail.emailAddress}
              error={disableSave && !validEmailAddress}
              sx={{ fontWeight: "normal", mt: 2 }}
              onChange={handleChangeEmailAddress}
              helperText={disableSave && !validEmailAddress && "Invalid email address"}
            />
            <TextField
              fullWidth
              select
              data-test="add-candidate-email-type"
              label="Email Type"
              value={convertStringToCandidateEmailType(newEmail.emailType)}
              sx={{ fontWeight: "normal", mt: 2 }}
              onChange={handleChangeEmailType}
            >
              {emailAddressTypeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  data-test={`add-candidate-email-type-${option.label}`}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: 1, mt: 1, justifyContent: "flex-end" }}
            >
              <Tooltip title="Cancel Edit" arrow>
                <Box>
                  <IconButton
                    data-test="cancel-edit-email"
                    color="info"
                    onClick={() => handleCancelChanges()}
                  >
                    <CancelOutlined />
                  </IconButton>
                </Box>
              </Tooltip>
              <Tooltip title={saveButtonMessage} arrow>
                <Box>
                  <IconButton
                    color="primary"
                    data-test="submit-edit-email"
                    onClick={() => handleSubmit()}
                    disabled={disableSave}
                  >
                    <Check />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          </>
        ) : (
          <>
            <span
              style={{
                minWidth: "19ch",
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setIsAddEmail(true)}
                data-test="add-email-button"
                hidden={!isAddEmail}
                endIcon={<Add />}
              >
                Add Email Address
              </Button>
            </span>
          </>
        )}
      </Box>
    </>
  );
};

export const DisplayAndEditPhoneNumber = ({
  candidatePhoneNumber,
}: {
  candidatePhoneNumber: ICandidatePhoneNumber;
}) => {
  const {
    getters: {
      isEditPhone,
      isValid,
      editPhone,
      phoneNumber,
      phoneType,
      disableSave,
      saveButtonMessage,
      validPhoneNumberEdit,
    },
    handlers: {
      setIsEditPhone,
      handleChangePhoneNumber,
      handleChangePhoneType,
      handleCancelChanges,
      handleSubmit,
    },
  } = useEditPhoneState(candidatePhoneNumber);

  const callCandidateAccess = useFeaturePermission(AppFeatureLookup.ProfileCallCandidateAction);
  const displayNumber = `${formatPhone(phoneNumber)} (${CandidatePhoneNumberType[phoneType]})`;

  return (
    <Box>
      {isEditPhone ? (
        <>
          <TextField
            fullWidth
            data-test="edit-candidate-phone"
            label="Edit Phone Number"
            error={disableSave && !validPhoneNumberEdit}
            value={editPhone.phoneNumberRaw}
            sx={{ fontWeight: "normal", mt: 2 }}
            onChange={handleChangePhoneNumber}
            helperText={disableSave && !validPhoneNumberEdit && "Invalid phone number"}
          />
          <TextField
            fullWidth
            select
            data-test="edit-candidate-phone-type"
            label="Edit Phone Type"
            value={convertStringToCandidatePhoneNumberType(editPhone.phoneType)}
            sx={{ fontWeight: "normal", mt: 2 }}
            onChange={handleChangePhoneType}
          >
            {phoneNumberTypeOptions.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                data-test={`edit-candidate-phone-type-${option.label}`}
              >
                {option.label}
                {Number(option.value) === phoneType ? " (Current)" : ""}
              </MenuItem>
            ))}
          </TextField>
          <Box
            sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: 1, mt: 1, justifyContent: "flex-end" }}
          >
            <Tooltip title="Cancel Edit" arrow>
              <Box>
                <IconButton data-test="cancel-edit-phone" color="info" onClick={() => handleCancelChanges()}>
                  <CancelOutlined />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={saveButtonMessage} arrow>
              <Box>
                <IconButton
                  color="primary"
                  data-test="submit-edit-phone"
                  onClick={() => handleSubmit()}
                  disabled={disableSave}
                >
                  <Check />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        </>
      ) : (
        <>
          {callCandidateAccess && isValid ? (
            <>
              <Link
                href={`tel:${phoneNumber}`}
                title={`Call ${phoneNumber}`}
                sx={{
                  textDecoration: "none",
                }}
              >
                {displayNumber}
              </Link>
              <IconButton
                title="Edit Phone Number"
                onClick={() => setIsEditPhone(true)}
                sx={{ ml: "0.5ch" }}
                size="small"
              >
                <Edit fontSize="small" />
              </IconButton>
            </>
          ) : (
            <Typography
              data-test="candidate-phone-number"
              component="span"
              sx={{ fontWeight: "normal", width: 1, display: "flex" }}
            >
              <DisplayPhoneNumber
                phone={candidatePhoneNumber}
                suffix={<> ({CandidatePhoneNumberType[candidatePhoneNumber.phoneType]})</>}
              />
              <IconButton
                title="Edit Phone Number"
                onClick={() => setIsEditPhone(true)}
                sx={{ ml: "0.5ch" }}
                size="small"
              >
                <Edit fontSize="small" />
              </IconButton>
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export const AddNewPhoneNumber = () => {
  const {
    getters: { isAddPhone, newPhone, disableSave, saveButtonMessage, validPhoneNumber },
    handlers: {
      setIsAddPhone,
      handleChangePhoneNumber,
      handleChangePhoneType,
      handleCancelChanges,
      handleSubmit,
    },
  } = useAddPhoneState();

  return (
    <>
      <Box>
        {isAddPhone ? (
          <>
            <TextField
              fullWidth
              data-test="add-candidate-phone"
              label="Add Phone Number"
              error={disableSave && !validPhoneNumber}
              value={newPhone.phoneNumberRaw}
              sx={{ fontWeight: "normal", mt: 2 }}
              onChange={handleChangePhoneNumber}
              helperText={disableSave && !validPhoneNumber && "Invalid phone number"}
            />
            <TextField
              fullWidth
              select
              data-test="add-candidate-phone-type"
              label="Set Phone Type"
              value={convertStringToCandidatePhoneNumberType(newPhone.phoneType)}
              sx={{ fontWeight: "normal", mt: 2 }}
              onChange={handleChangePhoneType}
            >
              {phoneNumberTypeOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  data-test={`add-candidate-phone-type-${option.label}`}
                >
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: 1, mt: 1, justifyContent: "flex-end" }}
            >
              <Tooltip title="Cancel Add" arrow>
                <Box>
                  <IconButton data-test="cancel-add-phone" color="info" onClick={() => handleCancelChanges()}>
                    <CancelOutlined />
                  </IconButton>
                </Box>
              </Tooltip>
              <Tooltip title={saveButtonMessage} arrow>
                <Box>
                  <IconButton
                    color="primary"
                    data-test="submit-add-phone"
                    onClick={() => handleSubmit()}
                    disabled={disableSave}
                  >
                    <Add />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          </>
        ) : (
          <>
            <span
              style={{
                minWidth: "19ch",
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setIsAddPhone(true)}
                data-test="add-phone-number-button"
                hidden={!isAddPhone}
                endIcon={<Add />}
              >
                Add Phone Number
              </Button>
            </span>
          </>
        )}
      </Box>
    </>
  );
};
